/* ==========================================================================
   Sticky add to cart — product pages

   The bar is a remote control for the real product form, not a second form:
     • size chips click the matching radio inside <variant-selects>
     • the Add button is type="submit" form="<product form id>"
   Dawn therefore stays the single source of truth. This file only mirrors state
   back into the bar and decides when it is on screen.
   ========================================================================== */
if (!customElements.get('product-sticky-bar')) {
  customElements.define(
    'product-sticky-bar',
    class ProductStickyBar extends HTMLElement {
      connectedCallback() {
        this.submit = this.querySelector('[data-psb-submit]');

        // Find the page's real add-to-cart form and adopt it. Doing this at
        // runtime rather than printing an id in Liquid means the bar keeps
        // working across all 59 product templates, and after any of them are
        // renamed or duplicated.
        this.form =
          document.querySelector('product-form form[action*="/cart/add"]') ||
          document.querySelector('form[action*="/cart/add"]');
        if (!this.form) return;

        // NOT `form.id`. A form exposes its named controls as properties, and the
        // cart form contains <input name="id">, so `form.id` returns that input
        // rather than the element's id attribute. getAttribute is the only safe
        // way to read or write it here.
        var formId = this.form.getAttribute('id');
        if (!formId) {
          formId = 'psb-adopted-product-form';
          this.form.setAttribute('id', formId);
        }
        this.submit.setAttribute('form', formId);

        this.liveSubmit =
          this.form.querySelector('.product-form__submit') ||
          document.querySelector('.product-form__submit');

        this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';

        /* Take the add-to-cart wording from the attribute, never from the
           rendered button. On a product whose selected variant is sold out the
           button reads "Sold out" at first paint; adopting that as defaultLabel
           meant switching to an in-stock variant restored the label to...
           "Sold out". The bar stayed dead for the rest of the session.

           The DOM fallback keeps working for any bar rendered before this
           attribute existed, but refuses to adopt the sold-out string. */
        var rendered = (this.querySelector('[data-psb-submit-label]') || this.submit).textContent.trim();
        this.defaultLabel = this.dataset.addLabel || '';
        if (!this.defaultLabel) {
          this.defaultLabel = rendered === this.soldOutLabel ? 'Add to cart' : rendered;
        }

        this.priceEl = this.querySelector('[data-psb-price]');
        this.sizeButtons = Array.from(this.querySelectorAll('[data-psb-size]'));
        this.selects = Array.from(this.querySelectorAll('[data-psb-select]'));
        this.submitLabel = this.querySelector('[data-psb-submit-label]');
        this.submitPrice = this.querySelector('[data-psb-submit-price]');
        this.dot = this.querySelector('[data-psb-dot]');

        this.watchAnchor();
        this.bindSizes();
        this.bindSelects();
        this.watchVariant();
        this.removeAttribute('hidden');
      }

      /* ---- when to show -------------------------------------------------- */

      get anchor() {
        // Show the bar once the page's own add-to-cart has scrolled away. Tying
        // it to a scroll offset instead would fire on short pages where the real
        // button is still perfectly visible.
        return this.liveSubmit;
      }

      watchAnchor() {
        var anchor = this.anchor;
        var self = this;

        if (!anchor || !('IntersectionObserver' in window)) {
          this.classList.add('is-visible');
          return;
        }

        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              self.classList.toggle('is-visible', !entry.isIntersecting);
            });
          },
          { rootMargin: '0px 0px -40px 0px' }
        );
        this.observer.observe(anchor);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      /* ---- driving the real picker --------------------------------------- */

      bindSizes() {
        var self = this;
        this.sizeButtons.forEach(function (button) {
          button.addEventListener('click', function () {
            var input = self.findInput(button.dataset.psbPosition, button.dataset.psbSize);
            if (!input) return;

            if (input.checked) return;
            input.checked = true;
            // `change` is what Dawn's variant-selects listens for; `input` is
            // dispatched too so any app watching either event stays in step.
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      }

      bindSelects() {
        var self = this;
        this.selects.forEach(function (select) {
          select.addEventListener('change', function () {
            var input = self.findInput(select.dataset.psbPosition, select.value);
            if (!input || input.checked) return;
            input.checked = true;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      }

      findInput(position, value) {
        var picker = document.querySelector('variant-selects');
        if (!picker) return null;

        var inputs = picker.querySelectorAll('input[type="radio"]');
        for (var i = 0; i < inputs.length; i++) {
          var input = inputs[i];
          var label = input.value || '';
          var matchesValue = label.trim() === String(value).trim();
          var matchesAxis = !position || input.name.indexOf('-' + position) === input.name.length - ('-' + position).length;
          if (matchesValue && matchesAxis) return input;
        }
        // Fall back to a value-only match — some option names contain a hyphen,
        // which makes the axis suffix check unreliable.
        for (var j = 0; j < inputs.length; j++) {
          if ((inputs[j].value || '').trim() === String(value).trim()) return inputs[j];
        }
        return null;
      }

      /* ---- mirroring state back ------------------------------------------ */

      watchVariant() {
        var self = this;

        /* Nothing here may depend on a specific node surviving.

           Dawn re-renders the product info on every variant change and swaps
           elements out, including <variant-selects> itself. The previous
           version observed that element directly, so after the first swap the
           observer was watching a detached node and never fired again — the bar
           kept whatever state it had. Pick a sold-out variant, then an in-stock
           one, and it stayed "Sold out" and disabled for the rest of the visit
           while Dawn's own button was correct.

           Three independent triggers now, because each has a failure mode:
             - Dawn's own pub/sub, when the theme exposes it
             - a MutationObserver on a container that is never replaced
             - change events, re-checked after Dawn's fetch has had time to land */

        /* Debounced with a timer, NOT requestAnimationFrame. rAF is suspended
           while a tab is in the background, so a shopper who picks a size,
           switches apps, and comes back would find the bar frozen on the
           previous variant. A timer still fires. */
        var timer = null;
        var queue = function () {
          window.clearTimeout(timer);
          timer = window.setTimeout(function () {
            self.sync();
          }, 16);
        };
        this.queueSync = queue;

        if (typeof window.subscribe === 'function') {
          try {
            window.subscribe('variant-change', queue);
          } catch (e) {
            /* older theme without that event — the observer below covers it */
          }
        }

        var scope =
          document.querySelector('.product__info-container') ||
          document.querySelector('.product') ||
          document.getElementById('MainContent') ||
          document.body;

        this.mutation = new MutationObserver(queue);
        this.mutation.observe(scope, { childList: true, subtree: true });

        document.addEventListener('change', function (event) {
          if (!event.target.closest) return;
          if (!event.target.closest('variant-selects, .product-form__input, product-info')) return;
          /* Immediately for the optimistic case, then again once Dawn's section
             fetch has replaced the markup. */
          queue();
          window.setTimeout(queue, 200);
          window.setTimeout(queue, 600);
        });

        this.sync();
      }

      sync() {
        /* Re-resolve rather than trusting the reference captured at init:
           Dawn's re-render can replace the form element as well. */
        if (!this.form || !document.contains(this.form)) {
          this.form =
            document.querySelector('product-form form') ||
            document.querySelector('form[action*="/cart/add"]') ||
            this.form;
        }
        var idInput = this.form && this.form.querySelector('input[name="id"]');
        var variantId = idInput && idInput.value;

        var picker = document.querySelector('variant-selects');
        var checked = picker
          ? Array.from(picker.querySelectorAll('input[type="radio"]:checked')).map(function (i) {
              return (i.value || '').trim();
            })
          : [];

        this.sizeButtons.forEach(function (button) {
          var on = checked.indexOf(String(button.dataset.psbSize).trim()) !== -1;
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        this.selects.forEach(function (select) {
          for (var i = 0; i < select.options.length; i++) {
            if (checked.indexOf(select.options[i].value.trim()) !== -1) {
              select.selectedIndex = i;
              break;
            }
          }
        });

        if (this.dot) {
          // The swatch colour lives on the chosen <option>; a native select
          // cannot render it, so it is mirrored onto the dot beside the control.
          var colourSelect = this.selects.filter(function (sel) {
            return sel.parentElement.querySelector('[data-psb-dot]');
          })[0];
          var chosen = colourSelect && colourSelect.options[colourSelect.selectedIndex];
          var swatch = chosen && chosen.getAttribute('data-swatch');
          if (swatch) this.dot.style.background = swatch;
        }

        // Price: copy Dawn's rendered markup so the money format, currency and
        // sale styling are guaranteed to match the page. (The visually-hidden
        // "Regular price" labels that come with it are re-hidden in the CSS.)
        var livePrice = document.querySelector('.price__container');
        if (this.priceEl && livePrice) this.priceEl.innerHTML = livePrice.innerHTML;

        // Availability: read the variant JSON, NOT the live button's `disabled`.
        // Dawn disables that button for two different reasons — sold out, and
        // "a request is in flight" — and copying the second one left this bar
        // permanently dead after the first add to cart.
        var available = true;
        var resolved = false;
        var json = document.querySelector('script[data-selected-variant]');
        if (json) {
          try {
            var parsed = JSON.parse(json.textContent);
            if (parsed) {
              /* Only trust it when it describes the variant actually selected.
                 During Dawn's re-render this tag can lag a beat behind the
                 form's id input. */
              if (!variantId || String(parsed.id) === String(variantId)) {
                available = !!parsed.available;
                resolved = true;
              }
            }
          } catch (e) {
            /* fall through to the live button */
          }
        }

        /* Last resort: mirror Dawn's own button, but only its text. Its
           `disabled` also goes true while an add-to-cart request is in flight,
           which is not the same thing as sold out. */
        if (!resolved && this.liveSubmit) {
          var liveText = (this.liveSubmit.textContent || '').trim();
          if (liveText) available = liveText.indexOf(this.soldOutLabel) === -1;
        }

        if (this.submit) {
          this.submit.disabled = !available;
          if (this.submitLabel) {
            this.submitLabel.textContent = available ? this.defaultLabel : this.soldOutLabel;
          } else {
            this.submit.textContent = available ? this.defaultLabel : this.soldOutLabel;
          }
        }

        // Price shown inside the button on the compact layout. Taken from Dawn's
        // rendered price so the money format matches, with the hidden a11y
        // labels stripped — they would otherwise read out inside the button name.
        if (this.submitPrice && livePrice) {
          var clone = livePrice.cloneNode(true);
          clone.querySelectorAll('.visually-hidden, .visually-hidden--inline').forEach(function (el) {
            el.remove();
          });
          var sale = clone.querySelector('.price-item--sale, .price-item--regular');
          this.submitPrice.textContent = (sale || clone).textContent.trim().split(/\s+/)[0] || '';
        }

        this.dataset.variantId = variantId || '';
      }
    }
  );
}
